/**
 * <pdp-pepper> — retouches JS de la colonne produit pour coller à la fiche Pepper.
 *
 * 1. Légendes des options : « Color: Black », « Size: M (…) » + badge de rareté.
 * 2. Le lien « Trouver ma taille » (bloc size-finder, sinon popup-link) est déplacé dans la
 *    légende de l'option taille de bande, avec une icône règle.
 * 3. Le prix est ajouté dans le bouton « Add to cart ».
 *
 * Horizon re-rend (morph) le variant-picker et les boutons d'achat à chaque
 * changement de variante : tout est ré-appliqué sur `variant:update` et via
 * un MutationObserver, appelé en synchrone (avant le rendu, donc sans flicker).
 * Chaque étape est idempotente.
 */
if (!customElements.get('pdp-pepper')) {
  customElements.define(
    'pdp-pepper',
    class extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section');
        this.onUpdate = () => {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.apply(), 80);
        };
        this.onResize = () => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.fitTitle(), 120);
        };
        // Légende mise à jour tout de suite au clic, sans attendre le re-rendu serveur (1,5 s et plus sur mobile).
        this.onChange = (event) => {
          if (event.target.closest && event.target.closest('variant-picker fieldset')) this.legendValue();
        };
        if (this.section) this.section.addEventListener('change', this.onChange);
        window.addEventListener('resize', this.onResize);
        document.addEventListener('variant:update', this.onUpdate);
        document.addEventListener('shopify:section:load', this.onUpdate);
        this.apply();
        const picker = this.section && this.section.querySelector('variant-picker');
        if (picker) {
          this.observer = new MutationObserver(() => {
            // Synchrone : le callback passe avant le rendu, donc pas de frame sans nos ajouts (flicker).
            if (!this.applying) this.apply();
          });
          this.observer.observe(picker, { childList: true, subtree: true });
        }
        const buy = this.section && this.section.querySelector('.buy-buttons-block');
        if (buy) {
          this.observerBuy = new MutationObserver(() => {
            // Synchrone : le callback passe avant le rendu, donc pas de frame sans nos ajouts (flicker).
            if (!this.applying) this.apply();
          });
          this.observerBuy.observe(buy, { childList: true, subtree: true });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('variant:update', this.onUpdate);
        document.removeEventListener('shopify:section:load', this.onUpdate);
        window.removeEventListener('resize', this.onResize);
        if (this.section) this.section.removeEventListener('change', this.onChange);
        if (this.observer) this.observer.disconnect();
        if (this.observerBuy) this.observerBuy.disconnect();
        clearTimeout(this.timer);
      }

      apply() {
        if (!this.section || this.applying) return;
        this.applying = true;
        try {
          this.legendValue();
          this.badge();
          this.sizeLink();
          this.fitTitle();
          this.atcPrice();
        } catch (e) {
          console.warn('pdp-pepper', e);
        } finally {
          // Oublie nos propres mutations pour ne pas relancer apply() en boucle.
          if (this.observer) this.observer.takeRecords();
          if (this.observerBuy) this.observerBuy.takeRecords();
          this.applying = false;
        }
      }

      fieldset(index) {
        return this.section.querySelector(`variant-picker fieldset[data-fieldset-index="${index}"]`);
      }

      legendValue() {
        // « Color: Black », « Size: M (…) » : valeur choisie dans chaque légende.
        this.section.querySelectorAll('variant-picker fieldset.variant-option--buttons').forEach((fs) => {
          const legend = fs.querySelector('legend');
          const checked = fs.querySelector('input:checked');
          if (!legend || !checked) return;
          let value = legend.querySelector('.pp-legend-value');
          if (!value) {
            value = document.createElement('span');
            value.className = 'pp-legend-value';
            legend.insertBefore(value, legend.querySelector('.pp-badge, .pp-size-btn'));
          }
          if (value.textContent !== checked.value) value.textContent = checked.value;
        });
      }

      badge() {
        // Référence gardée : le morph du variant-picker détruit le nœud déplacé,
        // on le ré-insère depuis la référence.
        if (!this.badgeNode) this.badgeNode = this.querySelector('.pp-badge');
        const badge = this.badgeNode;
        const fs = this.fieldset(this.dataset.swatchIndex);
        const legend = fs && fs.querySelector('legend');
        if (!badge || !legend) return;
        if (!legend.contains(badge)) legend.append(badge);
      }

      sizeLink() {
        // Cible du lien : le bloc « Calculateur de taille » s'il est présent, sinon
        // le bloc popup-link. Le vrai bloc reste à sa place (son <dialog> doit rester
        // hors du <form> du variant-picker). Dans la légende on met un bouton relais.
        if (!this.finder) this.finder = this.section.querySelector('size-finder');
        if (!this.popup) this.popup = this.section.querySelector('dialog-component.popup-link');
        const fs = this.fieldset(this.dataset.sizeLinkIndex);
        const legend = fs && fs.querySelector('legend');
        if ((!this.finder && !this.popup) || !legend) return;
        if (!this.linkNode) {
          const original = this.finder
            ? this.finder.querySelector('.size-finder__trigger')
            : this.popup.querySelector('.popup-link__button');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pp-size-btn';
          const icon = this.querySelector('template.pp-ruler');
          if (icon) button.append(icon.content.cloneNode(true));
          const label = document.createElement('span');
          label.textContent = (original ? original.textContent : '').trim();
          button.append(label);
          button.addEventListener('click', (event) => {
            event.preventDefault();
            if (this.finder && typeof this.finder.open === 'function') {
              this.finder.open();
              return;
            }
            const target = this.popup && this.popup.querySelector('.popup-link__button');
            if (target) target.click();
          });
          this.linkNode = button;
        }
        if (!legend.contains(this.linkNode)) legend.append(this.linkNode);
      }

      hasKaching() {
        return !!this.section.querySelector('[id*="kaching"], .kaching-bundles-product, .kaching-bundles__bar');
      }

      /** Réduit la taille du titre jusqu'à ce qu'il tienne sur une ligne (jamais agrandi). */
      fitTitle() {
        if (this.dataset.titleOneLine !== 'true') return;
        const title = this.section.querySelector('a[ref="productTitleLink"] [role="heading"]');
        if (!title) return;
        if (!this.titleBase) this.titleBase = parseFloat(getComputedStyle(title).fontSize) || 20;
        let size = this.titleBase;
        title.style.fontSize = '';
        let guard = 0;
        while (title.scrollWidth > title.clientWidth + 1 && size > 11 && guard < 40) {
          size -= 0.5;
          title.style.fontSize = size + 'px';
          guard += 1;
        }
      }

      atcPrice() {
        if (this.dataset.atcPrice !== 'true') return;
        const button = this.section.querySelector('.buy-buttons-block button[ref="addToCartButton"]');
        if (!button) return;
        if (this.hasKaching()) {
          // Kaching Bundles gère le texte et le prix du bouton : on ne le touche pas.
          const stale = button.querySelector('.pp-atc-price');
          if (stale) stale.remove();
          return;
        }
        const target =
          button.querySelector('.add-to-cart-text__content span span') ||
          button.querySelector('.add-to-cart-text__content');
        if (!target) return;
        const priceEl =
          this.section.querySelector('product-price [ref="priceContainer"]') ||
          this.section.querySelector('product-price');
        const text = (priceEl ? priceEl.textContent : '').replace(/\s+/g, ' ');
        const match = text.match(/[€$£]\s?\d[\d.,]*|\d[\d.,]*\s?[€$£]|[A-Z]{3}\s?\d[\d.,]*/);
        let span = target.querySelector('.pp-atc-price');
        if (!span) {
          span = document.createElement('span');
          span.className = 'pp-atc-price';
          target.append(span);
        }
        const wanted = match ? `${this.dataset.atcSeparator || ' - '}${match[0].trim()}` : '';
        if (span.textContent !== wanted) span.textContent = wanted;
      }
    }
  );
}
